import axios from 'axios';
import Cookies from 'js-cookie';
import store from '@store';
import { refreshToken } from './authRefresh.js';
import { EventBus } from '@utils/event-bus';
import router from "@router";
import { validateEmail } from "@utils/validateEmail";
import { buildAuth2FALogin } from '@utils/auth2faHelpers';
import { resolveSaleRefundErrorMessage } from '@src/enums/SaleRefundErrorEnum';

let fingerprintPromise = null;
let serverValidationPromise = null;
let fingerprintRequestId = null;
let isLoggingOut = false;

/**
 * Creates an axios instance with base configuration
 */
const standard = axios.create({
  baseURL: process.env.VUE_APP_API_HOST,
  timeout: 100000,
});

async function handleErrorResponse(err) {
  if (isLoggingOut) {
    return Promise.reject(err);
  }

  if (!err || !err.response) {
    notifyError(err, 'Erro ao executar a requisição');
    return Promise.reject(err || new Error('Erro desconhecido'));
  }

  const status = err.response.status;
  const responseData = err.response.data;

  const parsedData = parseResponseData(responseData);

  switch (status) {
    case 428:
      return handlePreconditionRequired(err, responseData);
    case 400:
      return handleBadRequest(err);
    case 401:
      return handleUnauthorized(err);
    case 404:
      return handleNotFound(err);
    case 422:
      return handleUnprocessableEntity(err, parsedData);
    case 500:
      return handleInternalServerError(err);
    default:
      return handleDefaultError(parsedData, err);
  }
}

/**
 * Parses response data from string to object if needed
 * @param {*} data - Response data
 * @returns {*} Parsed data
 */
function parseResponseData(data) {
  if (typeof data === 'string') {
    try {
      const trimmed = data.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.error(e);
    }
  }
  return data;
}

/**
 * Handles 428 Precondition Required error (fingerprint validation)
 * @param {Object} err - Error object
 * @param responseData
 * @returns {Promise} Promise with retry or rejection
 */
async function handlePreconditionRequired(err, responseData) {
  let requiresFingerprint = false;

  if (responseData) {
    if (typeof responseData === 'string') {
      try {
        const parsed = JSON.parse(responseData);
        requiresFingerprint = parsed.requires_fingerprint === true;
      } catch (e) {
        requiresFingerprint = false;
      }
    } else if (typeof responseData === 'object') {
      requiresFingerprint = responseData.requires_fingerprint === true;
    }
  }

  if (!requiresFingerprint) {
    notifyError(err, '428 - Falha na validação do dispositivo.');
    return Promise.reject(err);
  }

  if (err.config._retryWithFingerprint) {
    notifyError(err, '428 - Falha persistente na validação do dispositivo.');
    return Promise.reject(err);
  }

  const isOriginalRequest = !fingerprintPromise;

  if (isOriginalRequest) {
    fingerprintPromise = (async () => {
      try {
        await store.dispatch('fetchFingerprint');
        fingerprintRequestId = store.state.fingerprint && store.state.fingerprint.requestId
            ? store.state.fingerprint.requestId
            : '';
        return fingerprintRequestId;
      } catch (fingerprintError) {
        console.error('Erro ao buscar fingerprint:', fingerprintError);
        fingerprintPromise = null;
        serverValidationPromise = null;
        fingerprintRequestId = null;
        throw fingerprintError;
      }
    })();
  }

  try {
    await fingerprintPromise;

    if (isLoggingOut) return Promise.reject(err);

    if (isOriginalRequest) {
      err.config._retryWithFingerprint = true;
      err.config.headers['X-Fingerprint-RID'] = fingerprintRequestId;

      serverValidationPromise = standard.request(err.config)
          .finally(function() {
            fingerprintPromise = null;
            serverValidationPromise = null;
            fingerprintRequestId = null;
          });

      return serverValidationPromise;
    } else {
      if (serverValidationPromise) {
        try {
          await serverValidationPromise;
        } catch (ignoredError) {
          console.error(ignoredError);
        }
      }
      return standard.request(err.config);
    }
  } catch (fingerprintError) {
    fingerprintPromise = null;
    serverValidationPromise = null;
    fingerprintRequestId = null;

    if (!isLoggingOut) {
      notifyError(err, '428 - Falha ao validar dispositivo. Tente novamente.');
    }

    return Promise.reject(err);
  }
}

/**
 * Handles 400 Bad Request error
 */
function handleBadRequest(err) {
  notifyError(err, '400 - Parâmetro obrigatório ausente ou inválido.');
  return Promise.reject(err || new Error('400 - Bad Request'));
}

/**
 * Handles 401 Unauthorized error
 */
async function handleUnauthorized(err) {
  if (isLoggingOut) {
    return Promise.reject(err);
  }

  isLoggingOut = true;

  try {
    await store.dispatch("logoutRequest");
  } catch (error) {
    console.error('Logout failed:', error);
  } finally {
    isLoggingOut = false;
  }

  fingerprintPromise = null;
  serverValidationPromise = null;
  fingerprintRequestId = null;

  router.push('/');
  // Always shown: the session ends here, so a self-handled screen cannot report it.
  notifyError(err, '401 - Usuário não autorizado. Refaça o login e tente novamente', { always: true });
  return Promise.reject(err);
}

/**
 * Handles 404 Not Found error
 */
function handleNotFound(err) {
  notifyError(err, '404 - Recurso não encontrado.');
  return Promise.reject(err || new Error('404 - Not Found'));
}

/**
 * Handles 422 Unprocessable Entity error
 * @param {Object} err - Error object
 * @param {Object} parsedData - Parsed response data
 * @returns {Promise} Promise with redirection or rejection
 */
function handleUnprocessableEntity(err, parsedData) {
  if (
      err.response &&
      err.response.request &&
      err.response.request.responseType === 'blob'
  ) {
    notifyError(err, 'Erro ao solicitar o download deste arquivo');
    return Promise.reject(err);
  }

  const hasNeedAccessCode =
      parsedData &&
      parsedData.need_access_code &&
      err.config &&
      err.config.url &&
      err.config.url.includes('/oauth/token');

  if (hasNeedAccessCode) {
    return handleAccessCodeRedirect(err, parsedData);
  }

  let errorsPayload =
      parsedData ||
      (err.response && err.response.data) ||
      null;

  if (typeof errorsPayload === 'string') {
    try {
      errorsPayload = JSON.parse(errorsPayload);
    } catch (e) {
      console.error(e)
    }
  }

  if (
      errorsPayload &&
      typeof errorsPayload === 'object' &&
      errorsPayload.refresh_token
  ) {
    delete errorsPayload.refresh_token;
  }

  if (errorsPayload) {
    if (errorsPayload.errors && typeof errorsPayload.errors === 'object') {
      Object.keys(errorsPayload.errors).forEach(function (key) {
        const errorArray = errorsPayload.errors[key];
        // Skip notification if error message contains an email (privacy protection)
        // Prevents PII exposure in UI toasts until backend standardizes error messages
        if (
            Array.isArray(errorArray) &&
            errorArray[0] &&
            !validateEmail(errorArray[0])
        ) {
          notifyError(err, errorArray[0]);
        }
      });
    }

    else if (errorsPayload.messages) {
      Object.keys(errorsPayload.messages).forEach(function (key) {
        const message = errorsPayload.messages[key];

        if (typeof message === 'string' && !validateEmail(message)) {
          notifyError(err, message);
        }
      });
    }

    else if (errorsPayload.erro && typeof errorsPayload.erro === 'string') {
      notifyError(err, errorsPayload.erro);
    }

    else if (errorsPayload.message && typeof errorsPayload.message === 'string') {
      if (!validateEmail(errorsPayload.message)) {
        notifyError(err, errorsPayload.message);
      }
    }

    else if (Array.isArray(errorsPayload) && typeof errorsPayload[0] === 'string') {
      if (!validateEmail(errorsPayload[0])) {
        notifyError(err, errorsPayload[0]);
      }
    }
  }

  return Promise.reject(err);
}

/**
 * Handles access code redirect for 422 errors
 * @param {Object} err - Error object
 * @param {Object} parsedData - Parsed response data
 * @returns {Promise} Promise with redirection
 */
async function handleAccessCodeRedirect(err, parsedData) {
  const currentRoute = router.currentRoute;
  if (currentRoute && currentRoute.path === '/codigo-de-acesso') {
    return Promise.reject(err);
  }

  const accessCodeFlagRaw = parsedData.code_flag;
  const accessCodeFlag = accessCodeFlagRaw === true || String(accessCodeFlagRaw).toLowerCase() === 'true';

  let requestData = err.response && err.response.config ? err.response.config.data : null;

  if (requestData instanceof FormData) {
    const formDataOriginal = requestData;
    requestData = {};
    for (const [key, value] of formDataOriginal.entries()) {
      requestData[key] = value;
    }
  } else if (typeof requestData === 'string') {
    try {
      requestData = JSON.parse(requestData);
    } catch (e) {
      try {
        const params = new URLSearchParams(requestData);
        requestData = {};
        for (const [key, value] of params.entries()) {
          requestData[key] = value;
        }
      } catch (e2) {
        console.warn('Failed to parse request data for access code flow:', {
          originalData: requestData,
          jsonError: e.message,
          urlParamsError: e2.message
        });
        requestData = {};
      }
    }
  }

  if (requestData.isIntegration === 'true' || requestData.isIntegration === true) {
    requestData.isIntegration = true;
  } else if (requestData.isIntegration === 'false' || requestData.isIntegration === false) {
    requestData.isIntegration = false;
  }

  const effectiveType = (requestData && requestData.type)
      ? requestData.type
      : getUserType();

  const loginData = buildAuth2FALogin(requestData, parsedData, accessCodeFlag, effectiveType);

  try {
    await processAccessCodeNavigation(accessCodeFlag, loginData, parsedData);
    return Promise.reject(Object.assign({}, err, { isRedirect: true }));
  } catch (navError) {
    console.error('Failed to navigate to access code:', navError);
    notifyError(err, 'Erro ao redirecionar para validação. Tente novamente.');
    return Promise.reject(err);
  }
}

/**
 * Manages the side effects of the access code flow:
 * 1. Persists necessary data to Vuex store.
 * 2. Sets session flags.
 * 3. Redirects the user to the access code view.
 *
 * @param {boolean} accessCodeFlag - Indicates if the specific access code flow is triggered
 * @param {Object} loginData - The constructed login data payload
 * @param {Object} parsedData - The response data parsed from the backend error
 */
async function processAccessCodeNavigation(accessCodeFlag, loginData, parsedData) {
  // 1. Handle Store Persistence
  if (accessCodeFlag) {
    try {
      await store.dispatch('saveLoginDataForAccessCode', loginData);
    } catch (error) {
      console.error('Error saving loginData:', error);
      throw error;
    }
  } else {
    // Legacy flow: save refresh token if available
    if (parsedData && parsedData.refresh_token) {
      try {
        await store.dispatch("saveRefreshTokenForAccessCode", parsedData.refresh_token);
      } catch (error) {
        console.error('Error saving refresh_token:', error);
        throw error;
      }
    }
  }

  // 2. Set Session Flag
  sessionStorage.setItem('allow_access_code', 'true');

  // 3. Execute Routing
  const queryType = getUserType();
  const queryAccessCodeFlag = accessCodeFlag ? 'true' : undefined;

  // Returns the router's promise to be handled by the caller
  return router.push({
    path: '/codigo-de-acesso',
    query: {
      type: queryType,
      access_code_flag: queryAccessCodeFlag
    }
  });
}

/**
 * Handles 500 Internal Server Error
 * Truncates long messages (HTML dumps) to fit ~3 lines in UI toast
 */
function handleInternalServerError(err) {
  let errorMessage = '500 - Erro interno do servidor.';
  const MAX_LENGTH = 250;

  if (err && err.response && err.response.data) {
    const data = err.response.data;
    if (data && data.message && typeof data.message === 'string') {
      errorMessage = data.message;
    }
  }

  if (errorMessage.length > MAX_LENGTH) {
    errorMessage = errorMessage.substring(0, MAX_LENGTH) + '...';
  }

  notifyError(err, errorMessage);

  if (!err) {
    const serverError = new Error(errorMessage);
    serverError.response = { status: 500, data: { message: errorMessage } };
    return Promise.reject(serverError);
  }

  err.extractedMessage = errorMessage;
  return Promise.reject(err);
}

/**
 * Handles default errors
 * Prioritizes 'error' key (array/string) from API, falls back to 'message', then default text.
 * * @param {Object} parsedData - Parsed response data
 * @param parsedData
 * @param {Object} err - Error object
 * @returns {Promise} Rejected promise
 */
function handleDefaultError(parsedData, err) {
  let errorMessage = 'Erro ao executar a requisição';

  if (parsedData) {
    if (isErrorEnvelope(parsedData.error)) {
      // Standard envelope { error: { code, message } } (eng-docs error-handling Rule 1):
      // known codes get pt-BR copy, unknown ones fall back to the server message.
      errorMessage =
        resolveSaleRefundErrorMessage(parsedData.error.code) ||
        parsedData.error.message ||
        errorMessage;
    } else if (parsedData.error) {
      if (Array.isArray(parsedData.error) && parsedData.error.length > 0) {
        errorMessage = parsedData.error[0];
      } else if (typeof parsedData.error === 'string') {
        errorMessage = parsedData.error;
      }
    } else if (parsedData.message) {
      errorMessage = parsedData.message;
    }
  }

  notifyError(err, errorMessage);
  return Promise.reject(err);
}

/**
 * Whether `error` is the standard envelope body: an object with a string code and message.
 * @param {*} error - The `error` key of the response data
 * @returns {boolean}
 */
function isErrorEnvelope(error) {
  return Boolean(
    error &&
      typeof error === 'object' &&
      !Array.isArray(error) &&
      typeof error.code === 'string' &&
      typeof error.message === 'string'
  );
}

/**
 * Shows the error toast and flags the error, so callers can skip their own toast
 * instead of showing a second one for the same failure.
 * A request sent with `selfHandledError: true` gets no toast: its screen shows the
 * error itself (eng-docs front-end error-handling Rule 1).
 * @param {Object} err - Error object
 * @param {string} message - Toast message
 * @param {{ always?: boolean }} [options] - `always` ignores `selfHandledError`
 */
function notifyError(err, message, options) {
  const always = Boolean(options && options.always);
  if (!always && err && err.config && err.config.selfHandledError === true) {
    return;
  }

  if (err && typeof err === 'object') {
    err.errorNotified = true;
  }
  EventBus.$emit('errorNotify', message);
}

/**
 * Determines the current user type based on Vuex store state
 * @returns {string} 'CLIENT' or 'SELLER'
 */
function getUserType() {
  return store.getters["getIsClient"] ? 'CLIENT' : 'SELLER';
}

/**
 * Manages token refresh queue for concurrent requests
 */
const tokenRefreshManager = {
  isRefreshing: false,
  pendingRequests: [],

  processQueue: function(error, token) {
    this.pendingRequests.forEach(function(request) {
      if (error) {
        request.reject(error);
      } else {
        request.resolve(token);
      }
    });
    this.pendingRequests = [];
  },

  refreshAndUpdateConfig: async function(config) {
    this.isRefreshing = true;
    try {
      const token = await refreshToken();
      this.processQueue(null, token);
      if (token) {
        config.headers['Authorization'] = token;
      }
      return config;
    } catch (err) {
      this.processQueue(err, null);
      return Promise.reject(err);
    } finally {
      this.isRefreshing = false;
    }
  },

  addToQueue: function(config) {
    const self = this;
    return new Promise(function(resolve, reject) {
      self.pendingRequests.push({
        resolve: function(token) {
          if (token) {
              config.headers['Authorization'] = token;
          }
          resolve(config);
        },
        reject: reject
      });
    });
  }
};

standard.interceptors.response.use(
    function(response) {
      return response;
    },
    handleErrorResponse
);

const accessToken = Cookies.get('access_token');
if (accessToken) {
  standard.defaults.headers.common['Authorization'] = accessToken;
}

if (process.env.VUE_APP_FINGERPRINT_ENABLED === 'true') {
  standard.interceptors.request.use(
      async function (config) {
        if (!tokenRefreshManager.isRefreshing) {
          return tokenRefreshManager.refreshAndUpdateConfig(config);
        } else {
          return tokenRefreshManager.addToQueue(config);
        }
      },
      function(error) {
        return Promise.reject(error);
      }
  );
}

export default standard;